import React, { useState } from 'react';
import { Alert, Button, Spinner } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';

/**
 * Admin page that exposes manual triggers for the various on-demand jobs the
 * platform supports. The buttons here POST to the corresponding endpoint on
 * the backend, which enqueues a job onto the WebAppJobs queue. The actual
 * work is performed asynchronously by the JobProcessor worker; the user
 * can follow progress on the "Scheduler Logs" page.
 *
 * New schedulers can be added by appending to the `triggers` array below —
 * each entry is self-describing (label, description, button style, async
 * call) so the page does not need to be edited again until the trigger
 * itself changes.
 */
export const AdminSchedulers = () => {
  const [triggerStates, setTriggerStates] = useState({});
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'danger', message: string } | null

  const triggers = [
    {
      id: 'trading-strategy-execution',
      label: 'Trading Strategy Execution',
      description:
        'In admin-debug mode: runs only the strategies owned by you (the logged-in admin) ' +
        'that have "Enable debugging" checked on the Edit page. Useful for sanity-checking ' +
        'a single strategy end-to-end without firing every enabled strategy in the database. ' +
        'The daily 15:30 ET scheduler still runs every enabled strategy regardless of this flag.',
      color: 'primary',
      action: () => apiClient.triggerTradingStrategyExecution(),
    },
    {
      id: 'trading-strategy-amount-adjustment',
      label: 'Trading Strategy Amount Adjustment',
      description:
        'Runs the daily trading-strategy amount-adjustment pass: for every enabled strategy ' +
        'with "Adjust automatically" enabled, fetches the recent order history, computes the ' +
        'realised P&L of fills since the previous adjustment, and applies the delta to the ' +
        'TradeAmountUsd. The "since" cursor makes re-runs within the same window idempotent. ' +
        'The same job the daily scheduler enqueues at 16:30 ET.',
      color: 'info',
      action: () => apiClient.triggerTradingStrategyAmountAdjustment(),
    },
    {
      id: 'tickers-validation',
      label: 'Package Tickers Validation',
      description:
        'Runs the "Package tickers validation" report immediately: enumerates every CSV in ' +
        'the Analysis package folder, renders the PDF against the canonical S&P 500 1950 ' +
        'reference ticker, and (if the daily scheduler also enabled the notification type) ' +
        'sends a TickersValidationCompleted notification to every admin user that opted in via ' +
        'the Notification Preferences page. The same job the daily scheduler enqueues at ' +
        '11:30 UTC (30 min after the latest upstream collector — bond market activity at 11:00 UTC).',
      color: 'secondary',
      action: () => apiClient.triggerTickersValidation(),
    },
    {
      id: 'daily-candle-collection',
      label: 'Daily Candle Collection',
      description:
        'Enqueues the daily candle-collection job with the same parameters the automated ' +
        'scheduler uses at 08:00 UTC: collects Day-interval candles for all active tickers ' +
        'with a 7-day lookback window (SkipIfAnyCandleExist = false so late data from ' +
        'end-of-day sources is backfilled). Check the Scheduler Logs page for progress.',
      color: 'warning',
      action: () => apiClient.triggerDailyCandleCollection(),
    },
  ];

  const isLoading = (id) => !!triggerStates[id];

  const handleTrigger = async (trigger) => {
    if (!window.confirm(`Trigger "${trigger.label}" now?`)) {
      return;
    }

    setTriggerStates((s) => ({ ...s, [trigger.id]: true }));
    setFeedback(null);

    try {
      const ok = await trigger.action();
      if (ok) {
        setFeedback({
          type: 'success',
          message: `${trigger.label} has been queued. Check Scheduler Logs for progress.`,
        });
      } else {
        setFeedback({
          type: 'danger',
          message: `${trigger.label} trigger was rejected by the server.`,
        });
      }
    } catch (exc) {
      console.error(`Failed to trigger ${trigger.label}:`, exc);
      setFeedback({
        type: 'danger',
        message: `Failed to trigger ${trigger.label}: ${exc.message || exc}`,
      });
    } finally {
      setTriggerStates((s) => {
        const next = { ...s };
        delete next[trigger.id];
        return next;
      });
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2>Schedulers</h2>
      </div>

      {feedback && (
        <Alert color={feedback.type} toggle={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <div className="card mb-3">
        <div className="card-header">
          <h5 className="mb-0">Manual scheduler start</h5>
        </div>
        <div className="card-body">
          {triggers.map((trigger) => (
            <div
              key={trigger.id}
              className="d-flex align-items-start justify-content-between py-3 border-bottom"
            >
              <div className="me-3" style={{ maxWidth: '70%' }}>
                <div className="fw-semibold mb-1">{trigger.label}</div>
                <div className="text-muted small">{trigger.description}</div>
              </div>
              <div>
                <Button
                  color={trigger.color}
                  size="sm"
                  onClick={() => handleTrigger(trigger)}
                  disabled={isLoading(trigger.id)}
                >
                  {isLoading(trigger.id) ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Queuing…
                    </>
                  ) : (
                    trigger.label
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-muted small">
        Manual triggers enqueue the same job the automatic scheduler runs. Progress and
        errors are written to the Scheduler Logs page.
      </p>
    </div>
  );
};
